// @ts-expect-error TS(2306) FIXME: File '/Users/cedric/Dev/repositories/toktokdoc/wor... Remove this comment to see the full error message
import ToktokdocLib from '@saas/lib/toktokdoc-lib/web'
import SSN from 'french-ssn'
import moment from 'moment'
import { checkPasswordRules } from '@saas/lib/shared-utils'
import { CountryBirthError, InsNumError, PartialSsn, SsnObject, SsnValidationProps } from './types'
import { insNumMinLength } from '../components/Patient/Form/_utils'
import { patientFormErrors, patientFormInsNumErrors } from '../components/Patient/_errors'

const SSN_SEX_MATCH: Record<string, number[]> = {
    f: [2, 4, 6, 8],
    m: [1, 3, 5, 7],
}

const EXCLUDED_SSN_SEX = [5, 6, 7, 8]

/**
 * @description look if email is valid
 * @param {string} pEmail
 * @returns {string} return string or boolean
 */
export const isEmailValid = (pEmail?: string | null): string | boolean => {
    if (!pEmail) {
        return true
    }
    if (pEmail.length !== 0) {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,3}$/
        if (re.test(pEmail)) {
            return true
        }
        return 'E-mail incorrect'
    }
    return true
}

/**
 * @description look if contactPhone is valid
 * @param {string} contactPhone
 * @returns {string} return string or boolean
 */
export const isPhoneValid = (contactPhone: string | null | undefined, required = true): string | boolean => {
    if (contactPhone !== undefined && contactPhone !== null && contactPhone.length !== 0) {
        const cleanedContactPhone = contactPhone.replace(/ /g, '')
        if (
            /^(0|(00|\+)33)[1234589][0-9]{8}$/.test(cleanedContactPhone) ||
            /^(0|(00|\+)33)[67][0-9]{8}$/.test(cleanedContactPhone)
        ) {
            return true
        }
        return 'Le format de téléphone est invalide : il doit être composé de 10 chiffres sans caractères spéciaux'
    }
    return required ? 'Un numéro doit être fourni' : true
}

/**
 * @description look if postalCode is valid
 * @param {string} postalCode
 * @returns {string} return string or boolean
 */
export const isPostalCodeValid = (postalCode: string): string | boolean => {
    if (!postalCode || (postalCode && postalCode.length === 0)) {
        return 'Le CP doit être fourni'
    }
    if (/\D/.test(postalCode)) {
        return 'Caractère alphabétique impossible'
    }
    if (postalCode.length === 5) {
        return true
    }
    return 'Le CP doit contenir 5 chiffres'
}

/**
 * @description look if privatePhone is valid
 * @param {string} privatePhone
 * @returns {string} return string or boolean
 */
export const isPrivatePhoneValid = (privatePhone: string | null | undefined, required = false): string | boolean | undefined => {
    if (privatePhone !== undefined && privatePhone !== null && privatePhone.length !== 0) {
        const cleanedPrivatePhone = privatePhone.replace(/ /g, '')
        if (/^(0|(00|\+)33)[67][0-9]{8}$/.test(cleanedPrivatePhone)) {
            return true
        }
        if (/^(0|(00|\+)33)[1234589][0-9]{8}$/.test(cleanedPrivatePhone)) {
            return 'Le format de téléphone est invalide : il doit être composé de 10 chiffres sans caractères spéciaux et commencer par "06" ou "07"'
        }
        return 'Le format de téléphone est invalide : il doit être composé de 10 chiffres sans caractères spéciaux et commencer par "06" ou "07"'
    }
    return required ? 'Un numéro doit être fourni' : undefined
}

/**
 * @description look if password is valid
 * @param {string} password
 * @returns {string} return string or boolean
 */
export const isPasswordValid = (password?: string, confirm?: string) : string | boolean => {
    if (typeof password !== 'undefined' && password !== null && password.length !== 0) {
        const rules = checkPasswordRules(password, confirm ?? '')
        if (!rules.LENGTH) return 'Le mot de passe doit être de 8 caractères minimum'
        if (!rules.HAS_LETTER) return 'Le mot de passe doit contenir au moins 1 lettre'
        if (!rules.HAS_UPPERCASE_LETTER) return 'Le mot de passe doit contenir au moins 1 majuscule'
        if (!rules.HAS_SPECIAL_CHAR) return 'Le mot de passe doit contenir au moins un caractère spécial'
        if (!rules.HAS_NUMBER) return 'Le mot de passe doit contenir au moins 1 chiffre'
        if (typeof confirm !== 'undefined' && confirm !== null && confirm.length !== 0)
            if (!rules.MATCHES) return 'Les mots de passes sont différents'
        return true
    }
    return 'Veuillez saisir un mot de passe'
}

const getFormatedSsn = (ssn: string) => (ssn.trim().length === insNumMinLength ? ssn : undefined)

const getPartialSsn = (ssn: string): PartialSsn | undefined => {
    const insNum = getFormatedSsn(ssn)
    if (!insNum) return undefined

    try {
        const ssnObject: SsnObject = ToktokdocLib.Utils.ssnToObject(insNum)

        const { sex, year, month, department, municipality, order, nir } = ssnObject

        return {
            partialSsn: `${sex} ${year} ${month} ${department} ${municipality} ${order}`,
            keys: ssnObject,
            nir,
        }
    } catch (error) {
        console.error({
            error,
        })
    }

    return undefined
}

/**
 * @description Validate only if the nir key is correct insite the ssn number
 * @param {string} ins
 * @returns {string[]}
 */
const validateNirKey = ({ ssn }: Pick<SsnValidationProps, 'ssn'>): boolean => {
    const partialSsn = getPartialSsn(ssn)

    try {
        if (partialSsn) {
            const calculatedNir = SSN.getControlKey(partialSsn.partialSsn)

            return calculatedNir === partialSsn.nir
        }
    } catch (error) {
        console.error({ error })
    }
    return false
}

/**
 * @description Validate only if the nir key is correct insite the ssn number
 * @param {string} ins
 * @returns {string[]}
 */
const validateSsnSex = ({ ssn, sex }: Required<Pick<SsnValidationProps, 'sex' | 'ssn'>>): boolean => {
    const partialSsn = getPartialSsn(ssn)

    try {
        if (partialSsn) {
            const sexMatch = SSN_SEX_MATCH[sex]

            if (sexMatch.find((match) => match.toString() === partialSsn.keys.sex)) {
                return true
            }
            return false
        }
    } catch (error) {
        console.error({ error })
    }
    return false
}

/**
 * Returns acceptable birth years for a given SSN
 * Ex : 1923 ans 2023 for 23
 * @param {string} yearAsString
 * @returns {Array<Number>} acceptableYears
 */
const computeAcceptableYearViaSsn = (yearAsString: string): Array<number> => {
    const acceptableYears: Array<number> = []
    const ssnYearLastDigits = parseInt(yearAsString.substring(1, 3))
    const currentYear = new Date(Date.now()).getFullYear()
    const currentYearLastDigits = currentYear % 100
    const y = ((ssnYearLastDigits - currentYearLastDigits + 99) % 100) + 1
    acceptableYears.push(currentYear - 100 + y)
    if (ssnYearLastDigits <= currentYearLastDigits) acceptableYears.push(currentYear - 200 + y)
    return acceptableYears
}

/**
 * @description validate birthDate with ssn
 * @param {Date} date
 * @param {string} ssn
 * @returns {Array<InsNumError>} return string or undefined
 */
const validateBirthDateAndSsnMatching = (date: string, ssn: string): InsNumError[] => {
    const insErrors: InsNumError[] = []
    if (moment(date).isValid()) {
        const newBirthDate = new Date(date)
        if (SSN.validate(ssn)) {
            const {
                birth: { month },
            } = SSN.parse(ssn)
            const acceptableYears = computeAcceptableYearViaSsn(ssn)
            if ('index' in month) {
                let numberBirthMonth = month.index
                // if the numberBirthMonth is between 31 and 42, this means the month of birth is known (31 is January and 42 is december)
                if (numberBirthMonth >= 31 && numberBirthMonth <= 42) numberBirthMonth -= 30
                // we check, if INSNUM is between 1 and 12, that the latter corresponds to the month of birth entered
                if (numberBirthMonth !== newBirthDate.getMonth() + 1 && numberBirthMonth >= 1 && numberBirthMonth <= 12)
                    insErrors.push('birthMonthMismatch')
            }
            if (!acceptableYears.includes(parseInt(moment(newBirthDate).format('YYYY'))))
                insErrors.push('birthYearMismatch')
        }
    } else {
        insErrors.push('incorrectBirthYear')
    }

    return insErrors
}

/**
 * @description validate that the birth place isn't set to France when insNum at rank 6 and 7 equals 99
 * @param {number} birthPlaceInsee insee code given in birth place field, default is 100 for France
 * @param {string} ssn INS number
 * @returns {Array<CountryBirthError>} return string or undefined
 */
export const validateBirthPlaceAndSsnMatching = (birthPlaceInsee: number, ssn: string): CountryBirthError[] => {
    const countryBirthError: CountryBirthError[] = []

    const {
        birth: { country },
    } = SSN.parse(ssn)

    const isCountryUnknown = Object.prototype.hasOwnProperty.call(country, 'unknown')

    if (String(birthPlaceInsee).length > 3) birthPlaceInsee = parseInt(String(birthPlaceInsee).slice(-3))

    if ('insee' in country) {
        const inseeCodeFromSsn = parseInt(country.insee)
        // the type of insee code in db and in french-ssn lib mismatch, if we retrieve an insee code from db we only keep the last 3 digits
        if (
            (!isCountryUnknown && inseeCodeFromSsn !== birthPlaceInsee) ||
            (isCountryUnknown && birthPlaceInsee === 100)
        )
            countryBirthError.push('ssnMismatched')
    }
    return countryBirthError
}

export const runSsnValidation = ({ ssn, sex, birthDate, fromSearch = false }: SsnValidationProps): string | boolean => {
    const formatedSsn = getFormatedSsn(ssn)
    const partialSsn = getPartialSsn(ssn)
    const errors: InsNumError[] = []

    const isFullValidationEnabled = partialSsn?.keys.sex
        ? !EXCLUDED_SSN_SEX.includes(parseInt(partialSsn.keys.sex))
        : true

    if (formatedSsn) {
        const isNirKeyValid = validateNirKey({ ssn: formatedSsn })

        if (birthDate && isFullValidationEnabled) {
            const validationErrors = validateBirthDateAndSsnMatching(birthDate, formatedSsn)

            errors.push(...validationErrors)
        }

        if (!isNirKeyValid) {
            if (fromSearch) {
                errors.push('NirKeySearch')
            } else {
                errors.push('NirKey')
            }
        }

        if (sex && ['f', 'm'].includes(sex)) {
            const isSexValid = validateSsnSex({ ssn, sex })

            if (!isSexValid) {
                errors.push('sex')
            }
        }

        // check department code
        // 6th character must be a number
        // if 7th character is a letter, it must be A or B
        const sixthChar = formatedSsn.charAt(5)
        const seventhChar = formatedSsn.charAt(6)

        const sixthIsDigit = /^\d$/.test(sixthChar)
        if (!sixthIsDigit) {
            errors.push('departmentCode')
        }

        const seventhIsLetter = /[A-Z]/i.test(seventhChar)
        const allowedSeventhChars = ['A', 'B']

        if (seventhIsLetter && !allowedSeventhChars.includes(seventhChar.toUpperCase())) {
            errors.push('departmentCode')
        }

        return errors.length > 0
            ? errors.map((err) => (err ? patientFormInsNumErrors[err] : '')).join('. ')
            : errors.length === 0
    }
    return patientFormErrors.insNum?.minLength ?? ''
}
